import { InvitationCollectionAddResult, InvitationCollectionAddSkipped } from '../types/invitation-tracker.type'

/**
 * Fills missing skipped counts with `0` and derives `total_skipped` from the breakdown when absent.
 * @param data {InvitationCollectionAddResult | null | undefined} - Raw add-to-collection response.
 * @returns {InvitationCollectionAddResult | null} - Complete result, or `null` when the response is not an add result.
 */
export function withDefaultAddResult(data: InvitationCollectionAddResult | null | undefined): InvitationCollectionAddResult | null {
  if (!data || typeof data !== 'object' || !('eligible_entries' in data)) return null
  const skipped = {
    no_contact_info: data.skipped?.no_contact_info ?? 0,
    declined_in_event: data.skipped?.declined_in_event ?? 0,
    opted_out: data.skipped?.opted_out ?? 0,
    already_in_target_persona_collection: data.skipped?.already_in_target_persona_collection ?? 0
  }
  return {
    eligible_entries: data.eligible_entries ?? 0,
    total_skipped: data.total_skipped ?? sumSkippedEntries(skipped),
    skipped
  }
}

/**
 * Entries actually being added: eligible minus skipped (falls back to the skipped breakdown when `total_skipped` is absent).
 * @param result {InvitationCollectionAddResult | null} - Add-to-collection result.
 * @returns {number} - Added entries count (never negative).
 */
export function getAddedEntriesCount(result: InvitationCollectionAddResult | null): number {
  if (!result) return 0
  const totalSkipped = result.total_skipped ?? sumSkippedEntries(result.skipped)
  return Math.max(0, (result.eligible_entries ?? 0) - totalSkipped)
}

/**
 * Skipped-reason rows shown on the add result screen; rows with no entries are omitted.
 * @param result {InvitationCollectionAddResult | null} - Add result with defaults filled.
 * @returns {{ count: number; label: string }[]} - Rows in display order.
 */
export function buildAddSkippedRows(result: InvitationCollectionAddResult | null): { count: number; label: string }[] {
  const skipped = result?.skipped
  if (!skipped) return []
  return [
    { count: skipped.no_contact_info, label: 'No contact info (Skipped)' },
    { count: skipped.declined_in_event, label: 'Entries rejected in the event (Skipped)' },
    { count: skipped.opted_out, label: 'Opted out (Skipped)' },
    { count: skipped.already_in_target_persona_collection, label: 'Already in the target persona collection (Skipped)' }
  ].filter((row) => row.count >= 1)
}

/**
 * Sums the entries skipped for a given reason.
 * @param skipped {Partial<InvitationCollectionAddSkipped> | null | undefined} - Skipped entries breakdown.
 * @returns {number} - Total skipped entries count.
 */
function sumSkippedEntries(skipped: Partial<InvitationCollectionAddSkipped> | null | undefined): number {
  if (!skipped) return 0
  return (skipped.no_contact_info ?? 0) + (skipped.declined_in_event ?? 0) + (skipped.opted_out ?? 0) + (skipped.already_in_target_persona_collection ?? 0)
}
